import { useRef } from "react";
import { ArrowRight, ArrowUpRight, ChevronLeft, ChevronRight } from "lucide-react";
import ParallaxSection from "@/components/ParallaxSection";
import ImageSlider from "@/components/ImageSlider";
import BookingFields from "@/components/BookingFields";
import { useBooking } from "@/context/BookingContext";
import { destinations, orchidPatternImg, services, IconGlyph, banquet1Img, banquet2Img, conference1Img, conference2Img } from "@/data";
import heroExpImg from "@/assets/experiences/hero-exp.png";
import expAmenitiesBgImg from "@/assets/experiences/exp-bg-section.png";
import expFeatureFinalImg from "@/assets/experiences/experiences-collage-final.png";
import expFeatureMobile0Img from "@/assets/experiences/collage-mobile-0-rooted.png";
import expFeatureMobile1Img from "@/assets/experiences/collage-mobile-1.png";
import expFeatureMobile2Img from "@/assets/experiences/collage-mobile-3-jungle.png";
import wangalaBgImg from "@/assets/experiences/wangala-bg.png";
import wangalaFigureImg from "@/assets/experiences/wangala-figure.png";
import wangalaSlide1Img from "@/assets/experiences/wangala-slide-1.webp";
import wangalaSlide2Img from "@/assets/experiences/wangala-slide-2.jpg";
import wangalaSlide3Img from "@/assets/experiences/wangala-slide-3.webp";
import restaurantBannerBgImg from "@/assets/experiences/restaurant-banner-bg.webp";
import barImg from "@/assets/dining/setting-4.png";
import barBgImg from "@/assets/dining/bar-bg.png";

const barWineImg = "https://images.unsplash.com/photo-1553361371-9b22f78e8b1d?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";
const barWhiskeyImg = "https://images.unsplash.com/photo-1470337458703-46ad1756a187?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";
const barBottlesImg = "https://images.unsplash.com/photo-1779080415834-c13f4c682dc8?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";

const meetingVenues = [
  { name: "The Conference Room", cap: "100", styles: ["Theatre", "Classroom", "U-Shape", "Boardroom"] },
  { name: "The Grand Banquet Hall", cap: "300", styles: ["Banquet Round", "Cocktail", "Theatre"] },
];

export default function Experiences() {
  const { openBooking } = useBooking();
  const regionTrackRef = useRef(null);

  const scrollRegions = (dir) => {
    const track = regionTrackRef.current;
    if (!track) return;
    const card = track.querySelector(".region-card");
    const step = card ? card.getBoundingClientRect().width + 24 : 320;
    track.scrollBy({ left: dir * step, behavior: "smooth" });
  };

  return (
    <>
      <ParallaxSection image={heroExpImg} className="page-hero exp-hero" strength={16}>
        <div className="exp-hero-text">
          <p className="page-hero-eyebrow">
            <ArrowRight size={13} /> Experiences
          </p>
          <h1>
            Every trail, river and peak — <em>waiting to be explored.</em>
          </h1>
          <p className="page-hero-sub">Step outside, and Tura's landscape is right at your doorstep.</p>
        </div>

        <aside className="booking-card fade-up" data-testid="experiences-booking-panel">
          <div className="booking-card-head">
            <h3>Booking Online</h3>
            <p>Check availability in seconds.</p>
          </div>
          <BookingFields onSubmit={openBooking} idPrefix="experiences" />
        </aside>
      </ParallaxSection>

      {/* SPACES THAT ENRICH EVERY STAY — signature amenities, dark forest bg */}
      <section
        className="exp-amenities"
        data-testid="exp-amenities-section"
        style={{ backgroundImage: `url(${expAmenitiesBgImg})` }}
      >
        <div className="exp-am-inner">
          <div className="exp-am-head">
            <div className="exp-am-head-left fade-up">
              <p className="eyebrow light">
                <ArrowRight size={13} /> Signature Amenities
              </p>
              <h2>Spaces That Enrich Every Stay</h2>
              <p className="exp-am-sub">
                Thoughtfully designed experiences for relaxation, rejuvenation and wellbeing — inspired by the beauty of Meghalaya.
              </p>
            </div>
            <div className="exp-am-head-right fade-up">
              <a href="/#amenities-section" className="arrow-link light">
                View All Amenities <ArrowRight size={15} />
              </a>
            </div>
          </div>

          <div className="exp-am-track">
            {services.map((s, i) => (
              <article className="exp-am-card fade-up" key={s.title}>
                <div className="exp-am-media">
                  <img src={s.img} alt={s.title} loading="lazy" />
                  <span className="exp-am-badge">
                    <span className="exp-am-badge-icon">
                      <IconGlyph name={s.icon} />
                    </span>
                    0{i + 1}
                  </span>
                </div>
                <h3>{s.title}</h3>
                <p>{s.desc}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* MEETINGS & CELEBRATIONS — banquet + conference, real capacity figures */}
      <section className="exp-meetings" id="meetings">
        <div className="exp-meet-head fade-up">
          <p className="eyebrow">
            <ArrowRight size={13} /> Meetings &amp; Celebrations
          </p>
          <h2>
            Spaces built for <em>every occasion.</em>
          </h2>
          <p className="exp-meet-sub">
            From board meetings to full-scale weddings, The Vanda's Conference Room and Grand Banquet Hall are equipped to host up to 100 and 300 guests respectively.
          </p>
        </div>

        <div className="exp-meet-gallery">
          <img src={banquet1Img} alt="Banquet hall setup at The Vanda" className="fade-up" loading="lazy" />
          <img src={conference1Img} alt="Conference hall at The Vanda" className="fade-up" loading="lazy" />
          <img src={banquet2Img} alt="Banquet hall seating at The Vanda" className="fade-up" loading="lazy" />
          <img src={conference2Img} alt="Conference table at The Vanda" className="fade-up" loading="lazy" />
        </div>

        <div className="exp-meet-venues fade-up">
          {meetingVenues.map((v) => (
            <div className="exp-meet-venue" key={v.name}>
              <p className="exp-meet-venue-cap">{v.cap}</p>
              <p className="exp-meet-venue-capsub">Guests, maximum capacity</p>
              <h4>{v.name}</h4>
              <div className="exp-meet-venue-styles">
                {v.styles.map((s) => (
                  <span key={s}>{s}</span>
                ))}
              </div>
            </div>
          ))}
        </div>

        <button onClick={openBooking} className="btn btn-primary" data-testid="meetings-enquire">
          Enquire for Your Event <ArrowRight size={16} />
        </button>
      </section>

      {/* THE ORCHID TERRACE — restaurant teaser, full-bleed banner */}
      <section className="exp-restaurant" style={{ backgroundImage: `url(${restaurantBannerBgImg})` }}>
        <div className="exp-restaurant-overlay" aria-hidden="true" />
        <div className="exp-restaurant-copy fade-up">
          <p className="eyebrow light center">
            <ArrowRight size={13} /> The Orchid Terrace
          </p>
          <h2>
            A fitting end <em>to any day out.</em>
          </h2>
          <p>
            Whatever the day brings — a trek to Tura Peak, a quiet afternoon by the river — it ends the same way: back at The Orchid Terrace, with food that moves easily across Continental, North Indian, Pan-Asian and local Garo &amp; Khasi kitchens.
          </p>
          <a href="/dining" className="btn btn-outline light" data-testid="experiences-dining-link">
            Explore Dining <ArrowRight size={15} />
          </a>
        </div>
      </section>

      {/* THE VANDA BAR — craft cocktails, rooftop setting */}
      <section className="dn-bar" style={{ backgroundImage: `url(${barBgImg})` }}>
        <div className="dn-bar-overlay" aria-hidden="true" />
        <div className="dn-bar-inner">
          <div className="dn-bar-copy fade-up">
            <p className="eyebrow light center">
              <ArrowRight size={13} /> The Vanda Bar
            </p>
            <h2>
              Where the evening <em>slows down.</em>
            </h2>
            <p>
              Perched above the town, The Vanda Bar pairs a curated list of spirits and craft cocktails with sweeping views of the Garo Hills — a fitting close to a day in Tura, or a warm start to the evening ahead.
            </p>
            <a href="#book-now" onClick={openBooking} className="btn btn-outline light" data-testid="experiences-bar-cta">
              Reserve a Table <ArrowRight size={15} />
            </a>
          </div>
          <div className="dn-bar-collage fade-up">
            <img src={barImg} alt="Rooftop bar seating at The Vanda, at dusk" loading="lazy" />
            <img src={barWineImg} alt="Wine being poured at The Vanda Bar" loading="lazy" />
            <img src={barWhiskeyImg} alt="A whiskey being poured over ice at The Vanda Bar" loading="lazy" />
            <img src={barBottlesImg} alt="Single malts and spirits behind The Vanda Bar" loading="lazy" />
          </div>
        </div>
      </section>

      <div className="page-intro fade-up">
        <p>
          Every stay at The Vanda is an invitation to discover Tura at its own pace — from a peak at sunrise to a quiet afternoon by the river.
        </p>
      </div>

      {/* ROOTED. REAL. UNFORGETTABLE. — final composited collage image (desktop), stacked cards (mobile) */}
      <section className="exp-feature-flat fade-up exp-feature-flat-desktop">
        <img src={expFeatureFinalImg} alt="Curated experiences in Tura: Trek to Tura Peak, a day in the jungle by the river, and experience fishing." />
      </section>
      <section className="exp-feature-flat fade-up exp-feature-flat-mobile">
        <img src={expFeatureMobile0Img} alt="Rooted. Real. Unforgettable. Curated experiences that connect you with the soul of Tura." />
        <img src={expFeatureMobile1Img} alt="Trek to Tura Peak, and experience fishing." />
        <img src={expFeatureMobile2Img} alt="A day in the jungle, by the river — more than a place, it's a feeling." />
      </section>

      {/* EXPLORE THE REGION — same component language as the homepage */}
      <section className="region" data-testid="region-section-exp" style={{ backgroundImage: `linear-gradient(rgba(33,32,32,0.82), rgba(33,32,32,0.82)), url(${orchidPatternImg})` }}>
        <div className="region-head fade-up">
          <div>
            <p className="eyebrow light">
              <ArrowRight size={13} /> Explore the Region
            </p>
            <h2>
              Nearby, <em>and worth the trip.</em>
            </h2>
          </div>
          <div className="region-head-right">
            <p className="region-sub">Landmarks and landscapes within easy reach of The Vanda.</p>
            <div className="carousel-controls">
              <button onClick={() => scrollRegions(-1)} aria-label="Previous">
                <ChevronLeft />
              </button>
              <button onClick={() => scrollRegions(1)} aria-label="Next">
                <ChevronRight />
              </button>
            </div>
          </div>
        </div>
        <div className="region-grid" ref={regionTrackRef}>
          {destinations.map((d, i) => (
            <div key={d.name} className="region-card">
              <img src={d.img} alt={d.name} loading="lazy" />
              <span className="region-card-pill">0{i + 1}</span>
              <span className="region-card-body">
                <strong>{d.name}</strong>
                <em>{d.tag}</em>
              </span>
              <span className="region-card-arrow"><ArrowUpRight size={17} /></span>
            </div>
          ))}
        </div>
      </section>

      {/* SEASONAL NOTE — parallax accent, drummer figure bleeding from the left edge */}
      <ParallaxSection image={wangalaBgImg} className="wangala-section" strength={12}>
        <img src={wangalaFigureImg} alt="A Garo drummer performing at the Wangala Festival" className="wangala-figure" />
        <div className="wangala-copy">
          <p className="page-hero-eyebrow">
            <ArrowRight size={13} /> A Seasonal Note
          </p>
          <h2>
            The Wangala Festival — <em>a vivid window into local culture.</em>
          </h2>
          <p className="page-hero-sub">Time your stay around the Garo harvest celebration, marked by drums, dance and tradition.</p>
        </div>

        <div className="wangala-slider">
          <ImageSlider
            images={[
              { src: wangalaSlide1Img, alt: "Dancers performing at the Wangala Festival" },
              { src: wangalaSlide2Img, alt: "Drummers leading the Wangala Festival procession" },
              { src: wangalaSlide3Img, alt: "A dancer at the Wangala Festival, drummers beside her" },
            ]}
            cycleSeconds={5}
          />
        </div>
      </ParallaxSection>

      <section className="inner-cta-band">
        <p className="eyebrow">
          <ArrowRight size={13} /> Plan your days
        </p>
        <h2>
          Ready when <em>you are.</em>
        </h2>
        <button onClick={openBooking} className="btn btn-primary large" data-testid="experiences-cta-book">
          Book an Experience <ArrowRight size={17} />
        </button>
      </section>
    </>
  );
}
